<template>
  <div class="page t-container">
    <header v-if="!isLoading">
      <div
        v-if="!deliveryEmail"
        class="alert alert-primary alert-dismissible"
        role="alert"
        style="background-color:#41b4c3; color:#fff; width:100%; opacity: .8; cursor: pointer !important; padding: 5px; margin-bottom: 0"
      >
        <div class="text-center" @click="updateEmail()">
          The delivery location for this booking does not have an email. The
          recipient will not be able to receive tracking updates until this is
          set.<br />
          <b>Click this banner to update the email address.</b>
        </div>
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </header>

    <span class="page-subheading t-text-xs t-text-gray-600 t-font-medium">
      HOME | BOOKINGS | {{ booking.number }}
    </span>

    <div class="page-heading" role="heading">
      <h1 class="page-heading__header">Booking {{ booking.number }}</h1>
    </div>

    <div
      class="t-border t-border-gray-200 t-mt-10 t-shadow t-px-5 t-pt-3 t-pb-4 t-bg-white md:t-rounded-lg"
    >
      <!-- Card Heading -->
      <div class="t-flex t-justify-between t-items-baseline t-py-2">
        <div class="t-flex t-space-x-2 t-items-center">
          <p class="t-text-2xl t-font-bold t-mr-5">
            {{ booking.number }}
          </p>

          <span
            v-if="booking.status_id === 3"
            class="t-py-2 t-px-4 t-rounded t-bg-orange t-text-white t-font-semibold"
          >
            BOOKED
          </span>
          <span
            v-if="booking.status_id === 4"
            class="t-py-2 t-px-4 t-rounded t-bg-dodger-blue t-text-white t-font-semibold"
          >
            IN TRANSIT
          </span>
          <span
            v-if="booking.status_id === 9"
            class="t-py-2 t-px-4 t-rounded t-bg-red-500 t-text-white t-font-semibold"
          >
            CANCELLED
          </span>
          <span
            v-if="booking.status_id === 5"
            class="t-py-2 t-px-4 t-rounded t-bg-green-600 t-text-white t-font-semibold"
          >
            DELIVERED
          </span>

          <Button
            v-if="revertStatus"
            @click="revertBookingStatus()"
            type="error"
            size="large"
          >
            Revert Booking Status
          </Button>

          <Button
            type="success"
            size="large"
            v-if="booking.status_id <= 3"
            @click="markPickedUp"
          >
            Mark as picked up
          </Button>
        </div>
        <p v-if="booking.freight_payer" class="t-text-xl">
          ${{ booking.price }}
        </p>
      </div>

      <div
        class="t-flex t-flex-col md:t-flex-row md:t-justify-between t-border-t-2 t-border-gray-300 t-pb-2  md:t-pb-0"
      >
        <div
          class="t-grid t-grid-rows-3 t-grid-cols-1 t-text-sm t-py-1 md:t-grid-rows-1 md:t-grid-cols-3 md:t-divide-x-2 md:t-divide-gray-300"
        >
          <div class="t-px-2 t-py-1">
            <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
              Booking Reference
            </p>
            <p>{{ booking.sender_ref }}</p>
          </div>

          <div class="t-px-2 t-py-1">
            <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
              Placed
            </p>
            <p>{{ booking.placed_at }}</p>
          </div>

          <div class="t-px-2 t-py-1">
            <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
              Pickup Carrier
            </p>

            <span v-if="booking.status_id >= 3">{{ booking.carrier }}</span>
            <span v-else>
              Pending&nbsp;
              <span
                v-tooltip:bottom="[
                  '<h6>Why does it say pending?</h6> Because we\'re filling empty space on trucks around New Zealand it can\
                take a little while for us to confirm exactly who the carrier will be.\
                Your booking is 100% guaranteed at the price you booked at, as we have\
                a vast network of carriers around the country',
                  true
                ]"
              >
                <i class="far fa-info-circle"></i>
              </span>
            </span>
          </div>
        </div>

        <div
          class="t-flex t-flex-col t-space-y-3 t-space-x-2 t-text-md md:t-block md:t-space-y-2 md:t-text-sm md:t-py-2"
        >
          <a
            v-if="booking.carrier !== 'Aramex'"
            :href="'/send-freight/bookings/' + booking.id + '/all'"
            target="_blank"
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-dodger-blue t-ml-2 md:t-mb-2 hover:t-bg-blue-500 hover:t-text-white"
          >
            All Files
          </a>

          <a
            :href="'/send-freight/bookings/' + booking.id + '/address-label'"
            target="_blank"
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
            id="addr-label"
          >
            Address Label
          </a>

          <a
            :href="'/send-freight/bookings/' + booking.id + '/consignment'"
            target="_blank"
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
          >
            Con Note
          </a>

          <a
            :href="'/send-freight/bookings/' + booking.id + '/dgs'"
            target="_blank"
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
          >
            DGs
          </a>

          <a
            v-if="booking.has_pod"
            :href="
              '/send-freight/bookings/' + booking.id + '/proof-of-delivery'
            "
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
          >
            Proof of Delivery
          </a>

          <a
            v-if="booking.has_credit_note"
            :href="'/send-freight/bookings/' + booking.id + '/credit-note'"
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
          >
            Credit Note
          </a>

          <a
            v-if="booking.invoice_ready"
            :href="'/send-freight/bookings/' + booking.id + '/invoice'"
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-indigo-600 t-ml-2 md:t-mb-2 hover:t-bg-indigo-700 hover:t-text-white"
          >
            Invoice
          </a>

          <a
            :href="`/send-freight/bookings/${booking.id}/damage-claims`"
            target="_blank"
            class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-orange t-ml-2 md:t-mb-2 hover:t-bg-yellow-600 hover:t-text-white"
          >
            Damage Claims
          </a>
        </div>
      </div>
    </div>

    <section
      class="t-flex t-flex-col md:t-flex-row t-justify-between t-mt-8 t-space-y-4 md:t-space-y-0 t-space-x-0 md:t-space-x-8"
    >
      <div class="t-flex-grow t-bg-white t-rounded-md t-shadow t-p-5">
        <div class="t-border-b-2 t-border-gray-500 t-mb-4 t-pb-4">
          <p class="t-font-bold">Pickup Location</p>
        </div>
        <div>
          <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">
            From
          </p>

          <p class="t-font-bold">{{ pickupLocation.name }}</p>
          <p>
            {{ pickupLocation.address.street_number }}
            {{ pickupLocation.address.street_name }}
          </p>
          <p>{{ pickupLocation.address.suburb }}</p>
          <p>{{ pickupLocation.address.city }}</p>

          <p class="t-mt-2 t-mb-5">After {{ booking.pickup_at }}</p>

          <div
            class="t-flex t-flex-col md:t-flex-row t-space-x-0 md:t-space-x-20 t-space-y-2 md:t-space-y-0"
          >
            <div>
              <p class="t-text-xs t-uppercase t-text-gray-700 t-font-semibold">
                Sender Reference
              </p>
              <p>{{ pickupLocation.ref }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="t-flex-grow t-bg-white t-rounded-md t-shadow t-p-5">
        <div class="t-border-b-2 t-border-gray-500 t-mb-4 t-pb-4">
          <p class="t-font-bold">Delivery Location</p>
        </div>
        <div>
          <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">
            To
          </p>

          <p class="t-font-bold">{{ deliveryLocation.name }}</p>
          <p>
            {{ deliveryLocation.address.street_number }}
            {{ deliveryLocation.address.street_name }}
          </p>
          <p>{{ deliveryLocation.address.suburb }}</p>
          <p>{{ deliveryLocation.address.city }}</p>

          <p class="t-mt-2 t-mb-5">Before {{ booking.deliver_at }}</p>

          <div
            class="t-flex t-flex-col md:t-flex-row t-space-x-0 md:t-space-x-20 t-space-y-2 md:t-space-y-0"
          >
            <div>
              <p class="t-text-xs t-uppercase t-text-gray-700 t-font-semibold">
                Receiver Reference
              </p>
              <p>{{ deliveryLocation.ref }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="t-mt-8 t-rounded-md t-p-5 t-shadow t-bg-white">
      <h3 class="t-font-bold">Tracking History</h3>
      <div class="t-flex t-flex-justify-between t-mt-4">
        <div class="t-flex-grow t-mr-8 t-border-t-2 t-border-gray-400">
          <div
            v-if="featuredTimelineItem"
            class="t-mb-5 t-border-b-2 t-border-gray-400 t-pb-8"
          >
            <p class="t-mt-4 t-font-semibold">
              Most Recent Update
            </p>
            <p
              class="t-text-2xl t-font-semibold t-text-dodger-blue"
              :title="featuredTimelineItem.content"
            >
              {{ cutStrings(featuredTimelineItem.content, 35) }}
            </p>

            <div class="t-w-32">
              <p>{{ featuredTimelineItem.time.split(/AM|PM/)[1].trim() }}</p>
              <p>
                {{
                  featuredTimelineItem.time.substring(
                    0,
                    featuredTimelineItem.time.match(/AM|PM/).index + 2
                  )
                }}
              </p>
            </div>
          </div>

          <div v-else class="t-mt-4">
            <h5>Sorry, there is no information.</h5>
            <h5>Please contact Freighthub for more information.</h5>
          </div>

          <div
            v-for="(timelineItem, index) in timelineItems"
            class="t-flex t-mt-4 t-border-b t-border-gray-400 t-pb-4"
            :key="index"
          >
            <div class="t-w-32">
              <p>{{ timelineItem.time.split(/AM|PM/)[1].trim() }}</p>
              <p>
                {{
                  timelineItem.time.substring(
                    0,
                    timelineItem.time.match(/AM|PM/).index + 2
                  )
                }}
              </p>
            </div>
            <p :title="timelineItem.content">
              {{ cutStrings(timelineItem.content, 30) }}
            </p>
          </div>
        </div>
        <div class="t-w-10/20">
          <directions-map
            v-if="!isLoading"
            :origin="mapData.pickup"
            :destination="mapData.delivery"
            :latestPoint="mapData.latestPoint"
            :movements="rawMovements"
            :height="500"
            :key="mapKey"
            :in-transit="!(booking.status_id === 5 || booking.status_id === 9)"
          />
        </div>
      </div>
    </section>

    <section class="t-rounded-md t-mt-8 t-p-5 t-shadow t-bg-white">
      <h3 class="t-font-bold t-pb-4">Freight</h3>
      <div class="t-overflow-x-auto">
        <div
          class="t-align-middle t-inline-block t-min-w-full freight-table t-overflow-y-auto t-mt-2"
          role="table"
        >
          <div
            class="t-flex t-justify-between t-border-t-2 t-border-b-2 t-border-gray-500 t-mb-2 t-pt-4 t-pb-2 t-font-semibold"
          >
            <p class="t-w-14 t-flex">
              Quantity
            </p>
            <p class="t-w-36 t-px-3 content-center">
              Package Type
            </p>
            <p class="t-flex t-w-44 t-px-3">
              Type of Goods
            </p>
            <p class="t-flex t-w-44">
              Description
            </p>
            <p class="t-flex t-w-32 t-px-3">Weight (kg)</p>
            <p class="t-flex t-w-28 t-px-2">Volume (&#13221;)</p>
          </div>
          <div
            v-for="(freightLine, index) in freights"
            class="t-flex t-justify-between t-mb-2"
            :key="index"
          >
            <p class="t-w-14 t-flex">
              {{ freightLine.quantity }}
            </p>
            <p class="t-w-36 t-px-3 content-center">
              {{ freightLine.packageType }}
            </p>
            <p class="t-flex t-w-44 t-px-3">
              {{ freightLine.typeOfGoods }}
            </p>
            <p class="t-flex t-w-44">
              {{ freightLine.description }}
            </p>
            <p class="t-flex t-w-32 t-px-3">
              {{ Math.trunc(freightLine.weight) }} kgs
            </p>
            <p class="t-flex t-w-28 t-px-2">
              {{ freightLine.volume }} (&#13221;)
            </p>
          </div>

          <div
            class="t-flex t-justify-between t-mt-10 t-mb-2 t-border-t-2 t-border-b-2 t-border-gray-500 t-py-2"
          >
            <p class="t-w-14 t-flex">
              TOTAL
            </p>
            <p class="t-w-36 t-px-3 content-center"></p>
            <p class="t-flex t-w-44 t-px-3"></p>
            <p class="t-flex t-w-44"></p>
            <p class="t-flex t-w-32 t-px-3">
              {{ Math.trunc(getTotalWeight(freights)) }} kgs
            </p>
            <p class="t-flex t-w-28 t-px-2">
              {{ getTotalVolume(freights) }} (&#13221;)
            </p>
          </div>
        </div>
      </div>
    </section>
    <!--
    <Card style="margin-top: 20px">
      <Row slot="title">
        <Col :xs="{ span: 4 }" :sm="{ span: 8 }" :md="{ span: 1 }">
          <Button type="primary" size="large" @click="$router.go(-1)">
            <Icon type="md-arrow-round-back" size="15" />
          </Button>
        </Col>
        <Col :xs="{ span: 20 }" :sm="{ span: 16 }" :md="{ span: 5 }">
          <h3 style="margin-left: 10px; float: left; vertical-align: middle;">
            Booking {{ booking.id }}
          </h3>
        </Col>
        <Col :xs="{ span: 24 }" :sm="{ span: 14 }" :md="{ span: 10 }">
          <Dropdown class="float: right" style="margin-left: 30px;">
            <Button
              v-if="revertStatus"
              @click="revertBookingStatus()"
              type="error"
              size="large"
            >
              Revert Booking Status
            </Button>
            <div
              v-if="booking.freight_payer"
              type="text"
              size="large"
              style="cursor: default; margin-left: 20px; font-size: 16px; text-decoration: underline; color: #515a6e; display: inline-block"
            >
              ${{ booking.price }}
            </div>
            <Button
              @click="openClaims()"
              type="warning"
              size="large"
              style="margin-left: 20px"
            >
              Damage Claims
            </Button>
          </Dropdown>
        </Col>
        <Col :xs="{ span: 24 }" :sm="{ span: 10 }" :md="{ span: 4 }">
          <Button
            type="success"
            size="large"
            v-if="booking.status_id <= 3"
            @click="markPickedUp"
          >
            Mark as picked up
          </Button>
        </Col>
        <Col :xs="{ span: 24 }" :sm="{ span: 10 }" :md="{ span: 4 }"
          ><Dropdown>
            <Button type="primary" size="large">
              Download Files
              <Icon type="ios-arrow-down" />
            </Button>
            <DropdownMenu slot="list">
              <DropdownItem>
                <a target="_blank" :href="booking.id + '/all'">All Files</a>
              </DropdownItem>
              <DropdownItem>
                <a target="_blank" :href="booking.id + '/consignment'"
                  >Con Note</a
                >
              </DropdownItem>
              <DropdownItem>
                <a target="_blank" :href="booking.id + '/address-label'"
                  >Address Label</a
                >
              </DropdownItem>
              <DropdownItem v-if="booking.is_dg">
                <a target="_blank" :href="booking.id + '/dgs'">DGs</a>
              </DropdownItem>
              <DropdownItem v-if="booking.has_pod">
                <a target="_blank" :href="booking.id + '/proof-of-delivery'"
                  >Proof of Delivery</a
                >
              </DropdownItem>
              <DropdownItem>
                <a target="_blank" :href="booking.id + '/invoice'">Invoice</a>
              </DropdownItem>
              <DropdownItem v-if="booking.has_credit_note">
                <a target="_blank" :href="booking.id + '/credit-note'"
                  >Credit Note</a
                >
              </DropdownItem>
            </DropdownMenu>
          </Dropdown></Col
        >
      </Row>
      <Row class="row-blank"></Row>
      <Row type="flex" justify="center" align="middle">
        <Col :xs="{ span: 24 }" :md="{ span: 10 }">
          <location-card
            :location="pickupLocation"
            :location-icon="'out'"
          ></location-card>
        </Col>


        <Col :xs="{ span: 0 }" :md="{ span: 4 }">
          <div style="text-align: center; padding: 2em 0;">
            <h5>
              <u>{{ toReadableBookingStatus }}</u>
            </h5>
            <div style="min-width: 135px;">
              <Icon
                v-for="index in 3"
                :key="index"
                size="45"
                type="ios-arrow-forward"
              />
            </div>
          </div>
        </Col>

        <Col :xs="{ span: 24 }" :md="{ span: 0 }">
          <div style="text-align: center; padding: 2em 0;">
            <div style="min-width: 135px;">
              <Icon size="45" type="ios-arrow-down" />
            </div>
            <h5>
              <u>{{ toReadableBookingStatus }}</u>
            </h5>
            <div style="min-width: 135px;">
              <Icon size="45" type="ios-arrow-down" />
            </div>
          </div>
        </Col>
        <Col :xs="{ span: 24 }" :md="{ span: 10 }">
          <location-card
            :location="deliveryLocation"
            :location-icon="'in'"
          ></location-card>
        </Col>
      </Row>

      <Row class="row-blank"></Row>
      <Row type="flex" justify="center">
        <Col span="24">
          <Card>
            <h5
              slot="title"
              style="vertical-align: bottom; margin-bottom: 0 !important; padding-top: 0.25rem;"
            >
              <Icon type="ios-map" />Freight Movement
            </h5>

            <Row>
              <Col :sm="{ span: 24 }" :md="{ span: 8 }">
                <freight-movement-timeline
                  :timeline-items="timelineItems"
                  :booking="this.booking"
                  @unlinkTracker="unlinkTracker"
                ></freight-movement-timeline>
              </Col>
              <Col :sm="{ span: 24 }" :md="{ span: 16 }">
                <directions-map
                  v-if="!isLoading"
                  :origin="mapData.pickup"
                  :destination="mapData.delivery"
                  :latestPoint="mapData.latestPoint"
                  :movements="rawMovements"
                  :height="500"
                  :key="mapKey"
                  :in-transit="
                    !(booking.status_id === 5 || booking.status_id === 9)
                  "
                ></directions-map>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Row class="row-blank"></Row>
      <Row>
        <Col span="24">
          <freights-table
            :has-dgs="booking.is_dg"
            :table-data="freights"
          ></freights-table>
        </Col>
      </Row>
    </Card>
    -->

    <!--    <Spin size="large" fix v-if="isLoading"></Spin>-->
  </div>
</template>

<script>
import axios from "axios";
import BookingStatusStep from "../../components/steps/BookingStatus";
import LocationCard from "../../components/cards/Location";
import FreightsTable from "../../components/tables/FreightLines";
import FreightMovementTimeline from "../../components/timelines/FreightMovement";
import DirectionsMap from "../../components/maps/Directions";
import { mapMovementsToTimelineItems } from "../../utility/movements.js";

export default {
  name: "BookingDetails",
  components: {
    BookingStatusStep,
    LocationCard,
    FreightsTable,
    FreightMovementTimeline,
    DirectionsMap
  },
  props: ["bookingId", "revertStatus"],
  data() {
    return {
      booking: "",
      isLoading: true,
      bookingStatusStep: 0,
      times: {
        book: "",
        pickup: "",
        delivery: ""
      },
      bookingCarrier: "Pending",
      pickupLocation: {
        title: "pickup",
        name: "",
        address: "",
        times: [],
        ref: ""
      },
      deliveryLocation: {
        title: "delivery",
        name: "",
        address: "",
        times: [],
        ref: ""
      },
      freights: [],
      totalFreightWeight: 0,
      totalFreightVolume: 0,
      mapData: {
        center: {
          lat: "",
          lng: ""
        },
        pickup: {
          lat: "",
          lng: ""
        },
        delivery: {
          lat: "",
          lng: ""
        },
        latestPoint: {
          lat: "",
          lng: ""
        }
      },
      featuredTimelineItem: null,
      timelineItems: [],
      locationEmail: "",
      mapKey: 0,
      rawMovements: {
        data: []
      }
    };
  },
  methods: {
    cutStrings(str, max) {
      return str.length > max ? str.substr(0, max - 1) + "..." : str;
    },
    markPickedUp() {
      this.$Modal.confirm({
        title: "Picked Up",
        content:
          "<p>Are you sure you want to mark this booking as picked up?</p>",
        onOk: () => {
          axios
            .patch(this.booking.id + "/pickups")
            .then(() => {
              this.$Message.success("Marked the booking picked up");
              this.booking.status_id = 4;
            })
            .catch(e => {
              console.log(e);
              this.$Message.error("Failed to mark the booking picked up");
            });
        },
        onCancel: () => {}
      });
    },
    revertBookingStatus() {
      axios
        .get(this.bookingId + "/revert-status")
        .then(() => {
          this.$Message.success({
            content:
              "This bookings status has been reverted to Awaiting Pickup.",
            duration: 7
          });
          this.isLoading = true;
          this.loadBookingData();
          this.isLoading = false;
          this.revertStatus = false;
        })
        .catch(() => {
          this.$Message.error({
            content:
              "This bookings status could not be reverted. Please contact the Freighthub team to get this sorted.",
            duration: 7
          });
        });
    },
    markDelivered() {
      this.$Modal.confirm({
        title: "Delivered",
        content:
          "<p>Are you sure you want to mark this booking as delivered?</p>",
        onOk: () => {
          axios
            .patch(this.bookingId + "/deliveries")
            .then(response => {
              console.log(response);
              this.$Message.success("Marked the booking delivered");
            })
            .catch(e => {
              console.log(e);
              this.$Message.error("Failed to mark the booking delivered");
            });
        },
        onCancel: () => {}
      });
    },
    updateEmail() {
      this.$Modal.confirm({
        title: "Update Email",
        render: h => {
          return h("Input", {
            props: {
              value: this.locationEmail,
              autofocus: true,
              placeholder: "Please enter the email for this location..."
            },
            on: {
              input: val => {
                this.locationEmail = val;
              }
            }
          });
        },
        onOk: () => {
          axios
            .post(
              "/settings/locations/" +
                this.booking.deliveryLocation.data.id +
                "/updateLocationEmail",
              {
                email: this.locationEmail
              }
            )
            .then(response => {
              this.$Message.success("Email succesfully updated");
            })
            .catch(e => {
              this.$Message.error("Failed to update location email");
            });
        },
        onCancel: () => {}
      });
    },
    mapBookingStatus() {
      this.times.book = this.booking.placed_at;
      const statusId = this.booking.status_id;
      let stepId = 0;
      if (statusId === 3) {
        stepId = 1;
      }
      if (statusId === 4) {
        stepId = 2;
      }
      if (statusId === 5) {
        stepId = 4;
      }
      if (statusId === 9) {
        stepId = 5;
      }
      this.bookingStatusStep = stepId;
      this.bookingCarrier = this.booking.carrier;
    },
    mapPickupLocation() {
      this.pickupLocation.name = this.booking.pickupLocation.data.name;
      this.pickupLocation.address = this.booking.pickupLocation.data.address.data;
      this.pickupLocation.ref = this.booking.sender_ref;
      if (this.booking.pickup_at) {
        const time = {
          prefix: "",
          dateTime: this.booking.pickup_at
        };
        this.pickupLocation.times.push(time);
      }
      if (this.booking.pickup_before) {
        const time = {
          prefix: "-",
          dateTime: this.booking.pickup_before
        };
        this.pickupLocation.times.push(time);
      }
    },
    mapDeliveryLocation() {
      this.deliveryLocation.name = this.booking.deliveryLocation.data.name;
      this.deliveryLocation.address = this.booking.deliveryLocation.data.address.data;
      this.deliveryLocation.ref = this.booking.receiver_ref;
      if (this.booking.deliver_at) {
        const time = {
          prefix: "",
          dateTime: this.booking.deliver_at_hidden_time
        };
        this.deliveryLocation.times.push(time);
      }
    },
    mapFreight() {
      const that = this;
      this.booking.lines.data.forEach(function(line) {
        const freight = {
          quantity: line.quantity,
          packageType: line.packageType.data.name,
          typeOfGoods: line.goodsType.data.name,
          description: line.description,
          weight: Number.parseInt(line.weight),
          volume: line.volume,
          dgLines: line.dgLines,
          _expanded: line.dgLines.data.length > 0, //show dgLines when the freight is dg
          _disableExpand: line.dgLines.data.length < 1
        };
        that.freights.push(freight);
      });
    },
    getTotalWeight(freightLines) {
      let totalWeight = 0.0;
      for (let freightLine of freightLines) {
        totalWeight += parseFloat(freightLine.weight);
      }
      return totalWeight.toFixed(2);
    },
    getTotalVolume(freightLines) {
      let totalVolume = 0.0;
      for (let freightLine of freightLines) {
        totalVolume += parseFloat(freightLine.volume);
      }
      return totalVolume.toFixed(2);
    },
    mapMapData() {
      this.mapData.pickup.lat = Number.parseFloat(
        this.booking.pickupLocation.data.address.data.latitude
      );
      this.mapData.pickup.lng = Number.parseFloat(
        this.booking.pickupLocation.data.address.data.longitude
      );
      this.mapData.delivery.lat = Number.parseFloat(
        this.booking.deliveryLocation.data.address.data.latitude
      );
      this.mapData.delivery.lng = Number.parseFloat(
        this.booking.deliveryLocation.data.address.data.longitude
      );

      if (
        this.booking.latestEroadLocation &&
        this.booking.latestEroadLocation.data.last_location
      ) {
        this.mapData.latestPoint.lng = Number.parseFloat(
          this.booking.latestEroadLocation.data.last_location.lng
        );
        this.mapData.latestPoint.lat = Number.parseFloat(
          this.booking.latestEroadLocation.data.last_location.lat
        );
      } else {
        this.mapData.latestPoint.lng = null;
        this.mapData.latestPoint.lat = null;
      }
    },
    mapMovement() {
      if (typeof this.booking.movements != "undefined") {
        this.rawMovements = this.booking.movements;
        this.timelineItems = mapMovementsToTimelineItems(
          this.booking.movements.data,
          this.booking
        );

        if (this.timelineItems.length > 0) {
          this.featuredTimelineItem = this.timelineItems.pop();
          this.timelineItems = this.timelineItems.reverse();
        }
      } else {
        if (this.booking.placed_at) {
          this.featuredTimelineItem = {
            time: this.booking.placed_at,
            content: "Booking placed",
            required: true
          };
        }
      }
    },
    unlinkTracker() {
      axios
        .delete(
          "/send-freight/tracker/unlink/" +
            this.booking.tracker_id +
            "/" +
            this.booking.id
        )
        .then(res => {
          this.isLoading = true;
          setTimeout(() => {
            this.loadBookingData();
            this.$Message.success({
              content:
                "The tracker has successfully been un-linked from this booking.",
              duration: 7
            });
          }, 2000);
        })
        .catch(e => {
          this.$Message.error({
            content:
              "Sorry, something is not working correctly. We could not un-link this tracker, please contact Freighthub to un-link the tracker.",
            duration: 10,
            closable: true
          });
          throw e;
        });
    },
    loadBookingData() {
      axios
        .get("?t=" + Date.now())
        .then(response => {
          Object.assign(this.$data, this.$options.data.call(this));
          this.booking = response.data.data;
          this.mapBookingStatus();
          this.mapPickupLocation();
          this.mapDeliveryLocation();
          this.mapFreight();
          this.mapKey += 1;
          this.mapMapData();
          this.mapMovement();
        })
        .catch(e => {
          this.$Message.error({
            content:
              "Sorry, something is not working correctly. Please contact with Freighthub to get booking details.",
            duration: 10,
            closable: true
          });
          throw e;
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    moment: function(...args) {
      return moment(...args);
    }
  },
  created() {
    this.loadBookingData();
  },
  filters: {
    numFilter(value) {
      let realVal = Number(value).toFixed(2);
      return Number(realVal);
    }
  },
  computed: {
    deliveryEmail() {
      return !!this.booking.deliveryLocation.data.email;
    },
    toReadableBookingStatus() {
      switch (this.booking.status_id) {
        case 2:
          return "Booked";
        case 3:
          return "Booked";
        case 4:
          return "In Transit";
        case 5:
          return "Delivered";
        default:
          return "";
      }
    },
    subTrackerId() {
      if (this.booking.tracker_id !== null && !this.isLoading) {
        return this.booking.tracker_id.toString().substring(4);
      }
      return "";
    }
  }
};
</script>

<style scoped>
/* .timeline-content { 
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
} */
</style>
